import { useState, useContext, useEffect } from 'react';
import type { FC } from 'react';
import { Modal } from '@simplywallst/ui-core';
import { SplashPanel } from '@components/GuestRegistrationModal';
import { Row, Column } from '@simplywallst/ui-core';
import {
  FormHeader,
  FormSubHeader,
  SWSLogoBlack,
  FormIntroduction,
  GuestRegistration,
  TermsAndConditionsWrapper,
  Form,
  CloseIcon,
} from './styled';
import { TermsAndConditions } from '@components/Authentication';
import {
  AuthenticationSelect,
  Type,
} from '@components/Authentication/components/AuthenticationSelect';
import { Context as IOContext } from '@/context/IntersectionObserverDisabler';
import If from '@components/If';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';

const CareersUpsell = loadable(() => import('@components/CareersUpsell'), {
  resolveComponent: (component) => component.CareersUpsell,
});

export interface Props {
  onClose?: () => void;
  canClose?: boolean;
  removeWindowScroll?: boolean;
  initialType?: Type;
  onRegisterSuccess?: () => void;
  onLoginSuccess?: () => void;
}

const GuestRegistrationModal: FC<Props> = ({
  initialType,
  removeWindowScroll = false,
  onClose,
  canClose = false,
  onRegisterSuccess,
  onLoginSuccess,
}) => {
  const [type, setType] = useState(initialType);
  const [showCareersUpsell, setShowCareersUpsell] = useState(false);
  const context = useContext(IOContext);
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'guest_registration_modal',
  });
  useEffect(() => {
    if (typeof context.setEnabled === 'function') {
      /** we'll turn off intersection observer IF modal is shown */
      context.setEnabled(false);
    }
    return () => {
      if (typeof context.setEnabled === 'function') {
        context.setEnabled(true);
      }
    };
  }, [context]);

  const handleLoginError = () => {
    trackEvent({ action: 'failure', modifier: 'login' });
  };
  const handleRegisterError = () => {
    trackEvent({ action: 'failure', modifier: 'registration' });
  };

  return (
    <>
      <Modal
        onTamper={() => setShowCareersUpsell(true)}
        isNaked
        blurTargetId={'root'}
        removeWindowScroll={removeWindowScroll}
        onClose={() => {
          if (typeof onClose !== 'undefined') onClose();
        }}
      >
        <Row justifyContent="center" width="100%">
          <Column
            alignSelf="flex-start"
            alignSelfLg="center"
            justifyContent="center"
            displayFlex
          >
            <GuestRegistration data-cy-id="guest-registration-modal">
              <If match={canClose}>
                <CloseIcon
                  data-cy-id="close-button"
                  onClick={() => {
                    if (typeof onClose !== 'undefined') onClose();
                  }}
                />
              </If>
              <Form as="section" col={12} colLg={5}>
                <FormIntroduction>
                  <SWSLogoBlack />
                  <FormHeader>
                    Continue to Simply Wall St
                    <br /> stock analysis tool
                  </FormHeader>
                  <FormSubHeader>
                    Join 3,000,000 investors. Free Forever!
                  </FormSubHeader>
                  <AuthenticationSelect
                    current={type}
                    onChangeType={(type) => setType(type)}
                    onRegisterSuccess={onRegisterSuccess}
                    onLoginSuccess={onLoginSuccess}
                    onLoginError={handleLoginError}
                    onRegisterError={handleRegisterError}
                  />
                  <TermsAndConditionsWrapper>
                    <TermsAndConditions />
                  </TermsAndConditionsWrapper>
                </FormIntroduction>
              </Form>
              <SplashPanel introducing />
            </GuestRegistration>
          </Column>
        </Row>
      </Modal>
      <If match={showCareersUpsell && !canClose}>{() => <CareersUpsell />}</If>
    </>
  );
};

GuestRegistrationModal.defaultProps = {
  initialType: Type.Select,
};

export default GuestRegistrationModal;
